import { Params } from '@angular/router';
import { AppSettings } from '../shared/Constants/sharedConstants';

/** Query param that carries the in-app page a visitor was sent to login from. */
export const RETURN_URL_PARAM = 'returnUrl';

/** Query param that says why a tab was sent to login, so the login page can tell them. */
export const LOGIN_REASON_PARAM = 'reason';

/** Query param that opens the login page's forgotten-password dialog (`Request a new link`). */
export const FORGOT_PASSWORD_PARAM = 'forgot';

/**
 * Why a live session ended under a tab that did not choose to end it. `expired`: the server refused
 * its heartbeat. `signed-out-elsewhere`: the user signed out in another tab or device, or an
 * administrator signed them out. A tab that signed out itself carries no reason at all.
 */
export type LoginReason = 'expired' | 'signed-out-elsewhere';

const AUTH_PAGES = new Set(['login', 'logout']);

/**
 * The returnUrl arrives on the query string, so anyone can craft one. Only an in-app path survives:
 * it must be rooted, must not be protocol-relative (`//host`, or `/\host`, which browsers read the
 * same way), and must not carry control characters - browsers strip tabs and newlines from URLs, so
 * `/\t/host` is `//host` by the time it is followed. Today it only ever reaches navigateByUrl, which
 * cannot leave the app, but filtering here means a later `window.location = returnUrl` cannot become
 * an open redirect.
 *
 * Login/logout are refused so a stale link cannot bounce the user straight back to the form. The app
 * root (with or without the theme prefix) is refused too: it carries no destination of its own, and
 * passing it through would override the user's homepage preference with the root's dashboard redirect.
 */
export function safeReturnUrl(url: string | null | undefined): string | null {
  if (!url || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(url)) return null;

  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
  if (segments.some((s) => AUTH_PAGES.has(s.toLowerCase()))) return null;

  const theme = AppSettings?.UIAppConfig?.currentTheme?.themePathSegment;
  const pageSegments = theme && segments[0] === theme ? segments.slice(1) : segments;
  if (pageSegments.length === 0) return null;

  return url;
}

/** Query params for a login redirect that should return to `url`; empty when it is not worth returning to. */
export function returnUrlQueryParams(url: string | null | undefined): Params {
  const safe = safeReturnUrl(url);
  return safe ? { [RETURN_URL_PARAM]: safe } : {};
}
